import { z } from 'zod';
import { AppointmentSource } from '../enums.js';
import { CallerMessageSchema } from './call.js';
import { E164 } from '../primitives.js';
import { ServiceAnswerInputsSchema } from './serviceQuestions.js';
import { CustomerNameSchema } from './customerName.js';
/**
 * The uniform request every booking writer constructs (ADR-0007): the AI tool
 * call today, the dashboard "add appointment" and other channels later. All
 * receive the same `BookingResult`. `start` is an absolute instant (ISO 8601
 * with offset) — the BookingService re-validates it against business config.
 */
export const BookingRequestSchema = z.object({
    serviceId: z.string().min(1),
    /** ISO 8601 with timezone offset. */
    start: z.string().min(1),
    /** Trimmed, 1–120 characters, a real name (doc 65): see `CustomerNameSchema`. */
    customerName: CustomerNameSchema,
    customerPhone: E164.nullable().default(null),
    /** One free line the assistant heard and the calendar should carry
     *  (doc 23 P1). Defaulted, so every existing caller of this schema is
     *  unaffected. */
    note: z.string().max(300).default(''),
    /** A message taken EARLIER in the same call (doc 28 D3), copied onto the
     *  appointment as it is created. Null when none was taken — or when it comes
     *  later, which is written onto the appointment afterwards instead. */
    message: CallerMessageSchema.nullable().default(null),
    /** Answers to the service's questions (doc 64), checked against the service
     *  the writer holds — every one is required. Empty for a service with none. */
    serviceAnswers: ServiceAnswerInputsSchema.default([]),
    source: AppointmentSource.default('ai_call'),
    /** Present for AI-originated bookings; also the idempotency key. */
    callId: z.string().nullable().default(null),
    /** Set only when the caller asked for a specific staff member (doc 09 §7.3);
     * otherwise null and the server auto-assigns the first free eligible person. */
    staffId: z.string().nullable().default(null),
    /** Owner's browser test call, not a real customer (doc 13a §6). The booking is
     * still real and still holds the slot; it just never touches the client base
     * or the owner's KPIs. Defaults false so every real writer is unaffected. */
    test: z.boolean().default(false),
});
/** A resolver failure as a booking/change refusal — the one mapping every
 * writer uses, so they all say the same thing about the same answers. */
export function serviceAnswersRefusal(f) {
    const invalid = [...f.unknownIds, ...f.duplicateIds, ...f.tooLongIds];
    return {
        ok: false,
        reason: f.reason,
        ...(f.missing.length > 0 ? { missingQuestions: f.missing } : {}),
        ...(invalid.length > 0 ? { invalidQuestionIds: invalid } : {}),
    };
}
/**
 * Why a change to an EXISTING appointment was refused (doc 14 §4). Extends the
 * booking vocabulary — a reschedule can fail for any reason a booking can, plus
 * the lifecycle-specific ones below.
 *
 * `not_found` deliberately covers "no such appointment" AND "not the caller's",
 * so the tool cannot be used to probe which appointment ids exist (doc 14 §7).
 */
export const ChangeFailureReason = z.enum([
    'not_found',
    'not_authorized',
    'too_late',
    'already_changed',
    'slot_taken',
    'invalid',
    'closed',
    'past_notice',
    'unknown_service',
    'unknown_staff',
    'outside_booking_window',
    'service_answers_required',
    'invalid_service_answers',
]);
/** Move an existing appointment (doc 14 D3). `serviceId`/`staffId` null = keep. */
export const RescheduleRequestSchema = z.object({
    appointmentId: z.string().min(1),
    /** ISO 8601 with timezone offset, same contract as `BookingRequest.start`. */
    newStart: z.string().min(1),
    serviceId: z.string().nullable().default(null),
    staffId: z.string().nullable().default(null),
    /**
     * Answers for the DESTINATION service, only when the move changes the service
     * (doc 64). Empty on a same-service move, which keeps the booking's own
     * answers; answers given for a same-service move are refused, not dropped.
     */
    serviceAnswers: ServiceAnswerInputsSchema.default([]),
    /** Session id of the call/conversation making the change; the new appointment's `callId`. */
    callId: z.string().nullable().default(null),
    source: AppointmentSource.default('ai_call'),
    /** Owner's browser test call (doc 13a §6) — see `BookingRequestSchema.test`. */
    test: z.boolean().default(false),
});
//# sourceMappingURL=booking.js.map